import { z } from 'zod';
import { counter } from './counts.js';
import { planLimitsSchema, planUsageSchema } from './plan.js';
/**
 * The platform operator's view across every organisation.
 *
 * A separate contract from the tenant dashboard rather than a superset of it,
 * because the two answer different questions. Merging them would mean one
 * schema where half the fields are absent depending on who asked, and the
 * absent half is exactly the half that must not leak.
 */
const count = counter;
const tenantSummarySchema = z.object({
    orgId: z.string(),
    name: z.string(),
    contacts: count,
    sends: count,
    bounceRate: z.number().min(0).max(1),
    unknown: count,
    deadLettered: count,
});
export const platformOverviewSchema = z.object({
    totals: z.object({
        organisations: count,
        users: count,
        activeUsers: count,
        pendingInvitations: count,
        contacts: count,
        ownProvider: count,
        sendsToday: count,
        deadLetterDepth: count,
        providerErrorsLastHour: count,
    }),
    tenants: z.array(tenantSummarySchema),
});
/**
 * One account, as an operator screen shows it.
 *
 * No password material of any kind crosses this boundary — not the hash, not
 * its length, not whether one is set. What an operator needs is whether
 * somebody can get in, which `verifiedAt` and `lockedUntil` answer.
 */
const accountSummarySchema = z.object({
    userId: z.string(),
    email: z.string(),
    name: z.string(),
    orgId: z.string(),
    organisation: z.string(),
    /** Their role inside their own organisation. */
    role: z.string(),
    verifiedAt: z.string().datetime().nullable(),
    /** Only while still in the future; an expired lock is not a locked account. */
    lockedUntil: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const accountListSchema = z.object({ accounts: z.array(accountSummarySchema) });
const organisationSummarySchema = z.object({
    orgId: z.string(),
    name: z.string(),
    members: z.number().int(),
    contacts: z.number().int(),
    campaigns: z.number().int(),
    segments: z.number().int(),
    templates: z.number().int(),
    createdAt: z.string().datetime(),
    /** Which provider it sends through, or null for the deployment's own. */
    provider: z.string().nullable(),
    providerSince: z.string().datetime().nullable(),
    /** Standing with the platform: active, suspended or banned. */
    standing: z.string(),
    standingReason: z.string(),
    /** Messages a provider accepted in the last thirty days. */
    sends30: z.number().int(),
    lastSentAt: z.string().datetime().nullable(),
});
export const organisationListSchema = z.object({
    organisations: z.array(organisationSummarySchema),
});
/**
 * What an organisation has spent on the model.
 *
 * `since` travels with the rows so the screen can say what period it is
 * describing. A total with no window is a number people read as "ever", and
 * this one only goes back to the day recording started.
 */
const aiUsageSummarySchema = z.object({
    orgId: z.string(),
    organisation: z.string(),
    calls: z.number().int(),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    lastUsedAt: z.string().datetime().nullable(),
});
export const aiUsageListSchema = z.object({
    since: z.string().datetime(),
    usage: z.array(aiUsageSummarySchema),
});
/**
 * Moving an organisation to a plan.
 *
 * The plan is a bare string here and checked against the domain's list in the
 * route. A `z.enum` would be a second copy of that list, free to drift from
 * the one the limits hang off.
 */
export const assignPlanRequestSchema = z.object({
    plan: z.string().trim().min(1).max(40),
    /**
     * Why. Required to be present, allowed to be empty — a note nobody wrote is
     * honest, a field that did not exist means nobody was ever asked.
     */
    note: z.string().trim().max(500),
});
export const planCatalogueSchema = z.object({
    plans: z.array(z.object({ plan: z.string(), limits: planLimitsSchema })),
});
export const orgPlanSchema = z.object({
    plan: z.string(),
    /** When an operator decided, or null if nobody has looked at this one yet. */
    assigned: z.string().datetime().nullable(),
    note: z.string(),
    usage: planUsageSchema,
});
/**
 * Changing an organisation's standing with the platform.
 *
 * The standing is a bare string here and checked against the domain's list in
 * the route, the same arrangement plan names use: a `z.enum` would be a second
 * copy of that list, free to drift from the one the rules hang off.
 */
export const setStandingRequestSchema = z.object({
    standing: z.string().trim().min(1).max(20),
    /**
     * Why. Required to be present, allowed to be empty — a suspension nobody
     * wrote a reason for is honest; a field nobody was offered means nobody was
     * asked. Never sent to the organisation itself.
     */
    reason: z.string().trim().max(500),
});
export const setUserSuspendedRequestSchema = z.object({
    suspended: z.boolean(),
    reason: z.string().trim().max(500),
});
/** One line of what an operator did. Read by people, so it carries words. */
const auditEntrySchema = z.object({
    id: z.string(),
    at: z.iso.datetime(),
    /** Kept even when the operator is gone — "somebody" answers nothing. */
    actorEmail: z.string(),
    action: z.string(),
    subjectKind: z.string(),
    subjectId: z.string().nullable(),
    /** The name at the time. A later rename does not rewrite history. */
    subjectLabel: z.string(),
    detail: z.record(z.string(), z.unknown()),
});
export const auditListSchema = z.object({ entries: z.array(auditEntrySchema) });
/** One distinct failure, as the operator panel lists it. */
const errorGroupSchema = z.object({
    fingerprint: z.string(),
    kind: z.string(),
    route: z.string(),
    method: z.string(),
    /** Already masked: no ids, numbers or addresses from the original. */
    message: z.string(),
    occurrences: z.number().int(),
    firstSeen: z.iso.datetime(),
    lastSeen: z.iso.datetime(),
    lastOrgId: z.string().nullable(),
    /** Set when an operator has ticked it off. Cleared if it happens again. */
    resolvedAt: z.iso.datetime().nullable(),
});
export const errorListSchema = z.object({ errors: z.array(errorGroupSchema) });
export const replyTicketAsOperatorSchema = z.object({
    body: z.string().trim().min(1).max(10_000),
});
/**
 * Moving a thread between piles.
 *
 * The status is a bare string here and checked against the domain's list in
 * the route, the same arrangement plan names and standings use: a `z.enum`
 * would be a second copy of that list, free to drift from the one the rules
 * hang off.
 */
export const setTicketStatusSchema = z.object({
    status: z.string().trim().min(1).max(20),
});
//# sourceMappingURL=admin.js.map